import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatEuros } from "@sundial/core";
import { usePayments, useCreatePayment, useMarkReceived, useUpdatePayment, useDeletePayment, useClients } from "../api/queries.js";
import type { Payment } from "../api/queries.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { ClientFilter } from "../components/ClientFilter.js";
import { ImportCard } from "./ImportCard.js";

/* ── Helpers ──────────────────────────────────────────── */

/** Dropdown to assign a payment to a client. Empty value = unassigned. */
function ClientSelect({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
}) {
  const { data } = useClients();
  const clients = (data?.clients ?? []).filter((c) => !c.archived || c.id === value);
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height: "40px",
        borderRadius: "9px",
        border: "1px solid var(--border)",
        background: "var(--card)",
        color: "var(--text)",
        padding: "0 10px",
        fontSize: "14px",
        outline: "none",
      }}
    >
      <option value="">—</option>
      {clients.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

function todayIso(): string {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const parts = iso.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = parseInt(parts[1] ?? "1", 10);
  return `${parseInt(parts[2] ?? "1", 10)} ${months[m - 1]} ${parts[0]}`;
}

function statusBadge(status: Payment["status"]): { label: string; color: string; bg: string } {
  switch (status) {
    case "received":
      return { label: "Received", color: "var(--success, #22c55e)", bg: "rgba(34,197,94,.12)" };
    case "pending":
      return { label: "Pending", color: "var(--warning, #f59e0b)", bg: "rgba(245,158,11,.12)" };
    default:
      return { label: "Not landed", color: "var(--danger, #ef4444)", bg: "rgba(239,68,68,.12)" };
  }
}

function cardBorderColor(status: Payment["status"]): string {
  switch (status) {
    case "received":
      return "rgba(34,197,94,.25)";
    case "pending":
      return "rgba(245,158,11,.25)";
    default:
      return "rgba(239,68,68,.30)";
  }
}

/* ── Mark Received inline form ─────────────────────────── */

interface MarkReceivedFormProps {
  payment: Payment;
  onClose: () => void;
}

function MarkReceivedForm({ payment, onClose }: MarkReceivedFormProps) {
  const [amount, setAmount] = useState(
    payment.expectedCents ? (payment.expectedCents / 100).toFixed(2) : "",
  );
  const [date, setDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync } = useMarkReceived();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents) || cents <= 0) return;
    setError(null);
    try {
      await mutateAsync({ id: payment.id, receivedCents: cents, receivedDate: date });
      onClose();
    } catch {
      setError("Failed to save. Please try again.");
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        marginTop: "12px",
        padding: "14px",
        background: "var(--surface)",
        borderRadius: "10px",
        border: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
      aria-label="Mark received form"
    >
      <div
        style={{
          font: "600 11px/14px var(--font-head)",
          letterSpacing: ".07em",
          textTransform: "uppercase",
          color: "var(--muted2)",
        }}
      >
        Mark as received
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "120px" }}>
          <label
            htmlFor={`recv-amt-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            Amount received (€)
          </label>
          <div style={{ position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--muted)",
                fontFamily: "var(--font-mono)",
              }}
            >
              €
            </span>
            <input
              id={`recv-amt-${payment.id}`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "9px",
                border: "1px solid var(--border)",
                background: "var(--card)",
                color: "var(--text)",
                padding: "0 12px 0 28px",
                fontFamily: "var(--font-mono)",
                fontSize: "14px",
                outline: "none",
              }}
              required
            />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: "140px" }}>
          <label
            htmlFor={`recv-date-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            Date received
          </label>
          <input
            id={`recv-date-${payment.id}`}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "9px",
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              padding: "0 12px",
              fontSize: "14px",
              outline: "none",
            }}
            required
          />
        </div>
      </div>
      {error && (
        <div style={{ fontSize: "12px", color: "var(--danger, #ef4444)", marginTop: "4px" }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onClose}
          className="btn secondary"
          style={{ height: "36px", padding: "0 14px", fontSize: "13px" }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn primary"
          style={{ height: "36px", padding: "0 14px", fontSize: "13px" }}
        >
          Confirm
        </button>
      </div>
    </form>
  );
}

/* ── Edit Payment inline form ─────────────────────────── */

interface EditPaymentFormProps {
  payment: Payment;
  onClose: () => void;
}

function EditPaymentForm({ payment, onClose }: EditPaymentFormProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(
    payment.expectedCents ? (payment.expectedCents / 100).toFixed(2) : "",
  );
  const [expectedDate, setExpectedDate] = useState(payment.expectedDate ?? "");
  const [period, setPeriod] = useState(payment.period ?? "");
  const [invoiceRef, setInvoiceRef] = useState(payment.invoiceRef ?? "");
  const [invoiceUrl, setInvoiceUrl] = useState(payment.invoiceUrl ?? "");
  const [clientId, setClientId] = useState(payment.clientId ?? "");
  const isReceived = payment.receivedCents != null;
  const [receivedDate, setReceivedDate] = useState(payment.receivedDate ?? "");
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync } = useUpdatePayment();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents) || cents <= 0) return;
    const url = invoiceUrl.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      setError(t("payments.invoiceUrlInvalid"));
      return;
    }
    setError(null);
    const patch: Parameters<typeof mutateAsync>[0] = { id: payment.id, expectedCents: cents };
    if (expectedDate) patch.expectedDate = expectedDate;
    if (period) patch.period = period;
    // Sent even when empty so clearing the field removes the link (route maps "" → null).
    patch.invoiceRef = invoiceRef.trim();
    patch.invoiceUrl = url;
    patch.clientId = clientId === "" ? null : clientId;
    if (isReceived && receivedDate) patch.receivedDate = receivedDate;
    try {
      await mutateAsync(patch);
      onClose();
    } catch {
      setError("Failed to save. Please try again.");
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        marginTop: "12px",
        padding: "14px",
        background: "var(--surface)",
        borderRadius: "10px",
        border: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
      aria-label="Edit payment form"
    >
      <div
        style={{
          font: "600 11px/14px var(--font-head)",
          letterSpacing: ".07em",
          textTransform: "uppercase",
          color: "var(--muted2)",
        }}
      >
        {t("payments.edit")}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "120px" }}>
          <label
            htmlFor={`edit-amt-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            Amount (€)
          </label>
          <input
            id={`edit-amt-${payment.id}`}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "9px",
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              padding: "0 12px",
              fontFamily: "var(--font-mono)",
              fontSize: "14px",
              outline: "none",
            }}
            required
          />
        </div>
        <div style={{ flex: 1, minWidth: "140px" }}>
          <label
            htmlFor={`edit-date-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            Expected date
          </label>
          <input
            id={`edit-date-${payment.id}`}
            type="date"
            value={expectedDate}
            onChange={(e) => setExpectedDate(e.target.value)}
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "9px",
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              padding: "0 12px",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: "120px" }}>
          <label
            htmlFor={`edit-period-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            Period
          </label>
          <input
            id={`edit-period-${payment.id}`}
            type="text"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder="2026-05"
            pattern="\d{4}-\d{2}"
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "9px",
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              padding: "0 12px",
              fontFamily: "var(--font-mono)",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </div>
        {isReceived && (
          <div style={{ flex: 1, minWidth: "140px" }}>
            <label
              htmlFor={`edit-recv-${payment.id}`}
              style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
            >
              Received date
            </label>
            <input
              id={`edit-recv-${payment.id}`}
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "9px",
                border: "1px solid var(--border)",
                background: "var(--card)",
                color: "var(--text)",
                padding: "0 12px",
                fontSize: "14px",
                outline: "none",
              }}
            />
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "120px" }}>
          <label
            htmlFor={`edit-invref-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            {t("payments.invoiceRef")}
          </label>
          <input
            id={`edit-invref-${payment.id}`}
            type="text"
            value={invoiceRef}
            onChange={(e) => setInvoiceRef(e.target.value)}
            placeholder={t("payments.invoiceRefPlaceholder")}
            maxLength={50}
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "9px",
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              padding: "0 12px",
              fontFamily: "var(--font-mono)",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </div>
        <div style={{ flex: 1.4, minWidth: "160px" }}>
          <label
            htmlFor={`edit-invurl-${payment.id}`}
            style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}
          >
            {t("payments.invoiceUrl")}
          </label>
          <input
            id={`edit-invurl-${payment.id}`}
            type="url"
            inputMode="url"
            value={invoiceUrl}
            onChange={(e) => setInvoiceUrl(e.target.value)}
            placeholder="https://app.abby.fr/…"
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "9px",
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--text)",
              padding: "0 12px",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "160px" }}>
          <label style={{ fontSize: "12px", color: "var(--muted2)", display: "block", marginBottom: "4px" }}>
            {t("payments.client")}
          </label>
          <ClientSelect value={clientId} onChange={setClientId} ariaLabel={t("payments.client")} />
        </div>
      </div>
      {error && (
        <div style={{ fontSize: "12px", color: "var(--danger, #ef4444)", marginTop: "4px" }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onClose}
          className="btn secondary"
          style={{ height: "36px", padding: "0 14px", fontSize: "13px" }}
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          className="btn primary"
          style={{ height: "36px", padding: "0 14px", fontSize: "13px" }}
        >
          {t("payments.save")}
        </button>
      </div>
    </form>
  );
}

/* ── Payment row ──────────────────────────────────────── */

interface PaymentRowProps {
  payment: Payment;
}

function PaymentRow({ payment }: PaymentRowProps) {
  const [showMark, setShowMark] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { mutate: deletePayment } = useDeletePayment();
  const { data: clientsData } = useClients();
  const { t } = useTranslation();
  const badge = statusBadge(payment.status);
  const border = cardBorderColor(payment.status);
  const clientName = payment.clientId
    ? (clientsData?.clients.find((c) => c.id === payment.clientId)?.name ?? null)
    : null;

  const receivedCents = payment.receivedCents ?? 0;
  const barPct =
    payment.expectedCents > 0
      ? Math.min(100, Math.round((receivedCents / payment.expectedCents) * 100))
      : 0;
  const barColor =
    payment.status === "received"
      ? "var(--success, #22c55e)"
      : payment.status === "pending"
        ? "var(--warning, #f59e0b)"
        : "var(--danger, #ef4444)";

  const barLabel =
    payment.status === "received"
      ? `${formatEuros(receivedCents)} of ${formatEuros(payment.expectedCents)}`
      : `${formatEuros(receivedCents)} reçu / ${formatEuros(payment.expectedCents)} attendu`;

  return (
    <div
      style={{
        background: "var(--card)",
        border: `1px solid ${border}`,
        borderRadius: "16px",
        boxShadow: "0 1px 2px rgba(15,26,48,.04), 0 6px 18px rgba(15,26,48,.04)",
        padding: "16px 18px",
      }}
    >
      {confirmDelete && (
        <ConfirmDialog
          title={t("payments.confirmDelete")}
          body={t("payments.confirmDeleteBody", {
            period: payment.period ?? fmtDate(payment.expectedDate),
            amount: formatEuros(payment.expectedCents),
          })}
          confirmLabel={t("payments.delete")}
          danger
          onConfirm={() => {
            deletePayment(payment.id);
            setConfirmDelete(false);
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ font: "700 16px var(--font-head, Sora, sans-serif)" }}>
              {payment.period ?? "—"}
            </span>
            {clientName && (
              <span
                style={{
                  fontSize: "11.5px",
                  fontWeight: 600,
                  color: "var(--primary-deep, #D85F0E)",
                  background: "color-mix(in srgb, var(--primary) 12%, transparent)",
                  padding: "2px 8px",
                  borderRadius: "999px",
                }}
              >
                {clientName}
              </span>
            )}
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted)", marginTop: "2px" }}>
            Expected {fmtDate(payment.expectedDate)}
          </div>
          {payment.invoiceRef && (
            <div
              style={{
                fontSize: "12.5px",
                color: "var(--muted)",
                marginTop: "3px",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span style={{ color: "var(--muted2)" }}>{t("payments.invoiceRef")}</span>
              {payment.invoiceUrl ? (
                <a
                  href={payment.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--primary)", fontWeight: 600, textDecoration: "none" }}
                >
                  {payment.invoiceRef}
                  <span aria-hidden="true"> ↗</span>
                </a>
              ) : (
                <span style={{ fontWeight: 600, color: "var(--text)" }}>{payment.invoiceRef}</span>
              )}
            </div>
          )}
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            padding: "6px 11px",
            borderRadius: "999px",
            background: badge.bg,
            color: badge.color,
            font: "600 12px var(--font-head, Sora, sans-serif)",
            whiteSpace: "nowrap",
          }}
          aria-label={`Payment status: ${badge.label}`}
        >
          <span
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              background: badge.color,
              flexShrink: 0,
            }}
          />
          {badge.label}
        </span>

        {/* Edit / Delete actions */}
        <div style={{ display: "flex", gap: "6px", marginLeft: "4px" }}>
          <button
            onClick={() => { setShowEdit((v) => !v); setShowMark(false); }}
            title={t("payments.edit")}
            aria-label={t("payments.edit")}
            style={{
              width: "32px",
              height: "32px",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              background: "var(--card)",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✎
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            title={t("payments.delete")}
            aria-label={t("payments.delete")}
            style={{
              width: "32px",
              height: "32px",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              background: "var(--card)",
              color: "var(--danger, #ef4444)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          margin: "14px 0 6px",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "8px",
            borderRadius: "99px",
            background: "var(--surface)",
            overflow: "hidden",
          }}
          role="progressbar"
          aria-valuenow={barPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Payment progress: ${barPct}%`}
        >
          <div
            style={{
              height: "100%",
              width: `${barPct}%`,
              background: barColor,
              borderRadius: "99px",
              transition: "width .4s",
            }}
          />
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            color: "var(--muted)",
          }}
        >
          {barLabel}
        </div>
      </div>

      {/* Footer row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "10px",
        }}
      >
        <div style={{ fontSize: "12px", color: "var(--muted2)" }}>
          {payment.receivedDate ? `Received ${fmtDate(payment.receivedDate)}` : `Expected ${formatEuros(payment.expectedCents)}`}
        </div>

        {payment.status === "received" ? (
          <div
            style={{
              fontSize: "12.5px",
              color: "var(--success, #22c55e)",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12l4 4 10-10" />
            </svg>
            Received {fmtDate(payment.receivedDate)}
          </div>
        ) : (
          <button
            onClick={() => { setShowMark((v) => !v); setShowEdit(false); }}
            style={{
              height: "36px",
              padding: "0 14px",
              border: "1px solid var(--primary)",
              borderRadius: "9px",
              background: "none",
              color: "var(--primary)",
              font: "600 12.5px var(--font-head, Sora, sans-serif)",
              cursor: "pointer",
            }}
          >
            Mark received
          </button>
        )}
      </div>

      {/* Inline mark received form */}
      {showMark && (
        <MarkReceivedForm payment={payment} onClose={() => setShowMark(false)} />
      )}

      {/* Inline edit form */}
      {showEdit && (
        <EditPaymentForm payment={payment} onClose={() => setShowEdit(false)} />
      )}
    </div>
  );
}

/* ── Add Payment form ─────────────────────────────────── */

interface AddPaymentFormProps {
  onClose: () => void;
}

function AddPaymentForm({ onClose }: AddPaymentFormProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [period, setPeriod] = useState("");
  const [clientId, setClientId] = useState("");
  const { mutate } = useCreatePayment();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents) || cents <= 0) return;
    mutate({
      expectedCents: cents,
      ...(expectedDate ? { expectedDate } : {}),
      ...(period ? { period } : {}),
      ...(clientId ? { clientId } : {}),
    });
    onClose();
  }

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: "16px",
        padding: "18px",
        marginBottom: "14px",
      }}
    >
      <div
        style={{
          font: "700 15px var(--font-head, Sora, sans-serif)",
          marginBottom: "14px",
        }}
      >
        Add payment
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div>
          <label
            htmlFor="add-pay-amount"
            style={{
              font: "600 11px/14px var(--font-head)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              color: "var(--muted2)",
              display: "block",
              marginBottom: "6px",
            }}
          >
            Expected amount (€)
          </label>
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <span
              style={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--muted)",
                fontFamily: "var(--font-mono)",
              }}
            >
              €
            </span>
            <input
              id="add-pay-amount"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{
                width: "100%",
                height: "44px",
                borderRadius: "10px",
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                padding: "0 14px 0 30px",
                fontFamily: "var(--font-mono)",
                fontSize: "15px",
                outline: "none",
              }}
              required
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <label
              htmlFor="add-pay-exp-date"
              style={{
                font: "600 11px/14px var(--font-head)",
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--muted2)",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Expected date (optional)
            </label>
            <input
              id="add-pay-exp-date"
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              style={{
                width: "100%",
                height: "44px",
                borderRadius: "10px",
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                padding: "0 14px",
                fontSize: "14px",
                outline: "none",
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "140px" }}>
            <label
              htmlFor="add-pay-period"
              style={{
                font: "600 11px/14px var(--font-head)",
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--muted2)",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Period (optional, YYYY-MM)
            </label>
            <input
              id="add-pay-period"
              type="text"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2026-06"
              pattern="\d{4}-\d{2}"
              style={{
                width: "100%",
                height: "44px",
                borderRadius: "10px",
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                padding: "0 14px",
                fontFamily: "var(--font-mono)",
                fontSize: "14px",
                outline: "none",
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: "140px" }}>
            <label
              style={{
                font: "600 11px/14px var(--font-head)",
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--muted2)",
                display: "block",
                marginBottom: "6px",
              }}
            >
              {t("payments.client")}
            </label>
            <ClientSelect value={clientId} onChange={setClientId} ariaLabel={t("payments.client")} />
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "4px" }}>
          <button
            type="button"
            onClick={onClose}
            className="btn secondary"
            style={{ height: "40px", padding: "0 16px" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn primary"
            style={{ height: "40px", padding: "0 18px" }}
          >
            Add payment
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── Payments screen ──────────────────────────────────── */

/* ── Period summary (year → month drill) ──────────────── */

interface PeriodAgg {
  key: string; // "2026" (year) or "2026-04" (month)
  expectedCents: number;
  receivedCents: number;
  remainingCents: number;
  count: number;
}
// "client" grouping was removed: the CLIENT filter is now the client axis, and
// grouping is a separate, purely temporal axis. See SundialPaymentsToolbar.
type GroupMode = "all" | "year" | "month";


const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function periodKey(p: Payment): string | null {
  if (p.period && /^\d{4}-\d{2}/.test(p.period)) return p.period.slice(0, 7);
  if (p.expectedDate) return p.expectedDate.slice(0, 7);
  if (p.receivedDate) return p.receivedDate.slice(0, 7);
  return null;
}

function monthLabel(ym: string): string {
  const [yr, mo] = ym.split("-");
  return `${MONTH_NAMES[parseInt(mo ?? "1", 10) - 1]} ${yr}`;
}

function addInto(g: PeriodAgg, p: Payment): void {
  const received = p.receivedCents ?? 0;
  g.expectedCents += p.expectedCents;
  g.receivedCents += received;
  g.remainingCents += Math.max(0, p.expectedCents - received);
  g.count += 1;
}

function aggregate(payments: Payment[], mode: GroupMode): PeriodAgg[] {
  if (mode === "all") {
    const all: PeriodAgg = { key: "all", expectedCents: 0, receivedCents: 0, remainingCents: 0, count: 0 };
    for (const p of payments) addInto(all, p);
    return all.count ? [all] : [];
  }
  const map = new Map<string, PeriodAgg>();
  for (const p of payments) {
    const ym = periodKey(p);
    if (!ym) continue;
    const key = mode === "year" ? ym.slice(0, 4) : ym;
    let g = map.get(key);
    if (!g) {
      g = { key, expectedCents: 0, receivedCents: 0, remainingCents: 0, count: 0 };
      map.set(key, g);
    }
    addInto(g, p);
  }
  return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));
}

function groupLabel(key: string, mode: GroupMode): string {
  if (mode === "all") return "All time";
  if (mode === "year") return key;
  return monthLabel(key);
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
      <div
        style={{
          font: "600 9px/12px var(--font-head)",
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: "var(--muted2)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          fontSize: "14px",
          color: color ?? "var(--text)",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function MetricRow({ agg }: { agg: PeriodAgg }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0,1fr))",
        gap: "10px",
        marginTop: "10px",
      }}
    >
      <Metric label="Received" value={formatEuros(agg.receivedCents)} color="var(--success, #22c55e)" />
      <Metric
        label="Remaining"
        value={formatEuros(agg.remainingCents)}
        color={agg.remainingCents > 0 ? "var(--danger, #ef4444)" : "var(--muted)"}
      />
      <Metric label="Expected" value={formatEuros(agg.expectedCents)} />
      <Metric label="Payments" value={String(agg.count)} />
    </div>
  );
}


const GROUP_MODES: { value: GroupMode; label: string }[] = [
  { value: "all", label: "Tout" },
  { value: "year", label: "Par an" },
  { value: "month", label: "Par période" },
];

/** GROUPER segment — the "how to bucket" axis (temporal only). Active segment is
    orange-filled + checked, matching the shared toggle style across the app. */
function GrouperSegment({ mode, onChange }: { mode: GroupMode; onChange: (m: GroupMode) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Grouper les paiements"
      style={{ display: "inline-flex", gap: 4, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 11, padding: 4 }}
    >
      {GROUP_MODES.map((g) => {
        const active = mode === g.value;
        return (
          <button
            key={g.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(g.value)}
            style={{
              height: 28,
              padding: "0 12px",
              borderRadius: 8,
              cursor: "pointer",
              font: active ? "700 12px Sora" : "600 12px Sora",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              whiteSpace: "nowrap",
              transition: ".14s",
              border: active ? "1px solid var(--primary)" : "1px solid transparent",
              background: active ? "var(--primary)" : "transparent",
              color: active ? "#fff" : "var(--muted)",
              boxShadow: active ? "0 2px 8px var(--glow)" : "none",
            }}
          >
            {active && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12l4 4 10-10" />
              </svg>
            )}
            {g.label}
          </button>
        );
      })}
    </div>
  );
}

function PeriodSummary({ payments, mode }: { payments: Payment[]; mode: GroupMode }) {
  const groups = aggregate(payments, mode);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "14px" }}>
      {groups.map((g) => (
        <div
          key={g.key}
          style={{
            borderRadius: "16px",
            background: "var(--card)",
            border: "1px solid var(--border)",
            padding: "14px 16px",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-head)",
              fontWeight: 700,
              fontSize: "16px",
              color: "var(--text)",
            }}
          >
            {groupLabel(g.key, mode)}
          </span>
          <MetricRow agg={g} />
        </div>
      ))}
    </div>
  );
}

export function Payments() {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState<GroupMode>("year");
  const { data, isLoading } = usePayments();
  const { data: clientsData } = useClients();

  const allPayments = data?.payments ?? [];
  // Scope to the selected client (its own payments only). Owed recomputes for
  // the filtered set, so the hero reflects what's shown.
  const payments = clientId ? allPayments.filter((p) => p.clientId === clientId) : allPayments;
  const totalOwedCents = clientId
    ? payments.reduce((s, p) => s + (p.status === "received" ? 0 : p.expectedCents - (p.receivedCents ?? 0)), 0)
    : (data?.totalOwedCents ?? 0);
  const filterName = clientId ? (clientsData?.clients.find((c) => c.id === clientId)?.name ?? "") : "";
  const filterInitial = (filterName.trim()[0] ?? "?").toUpperCase();
  const hasMultipleClients = (clientsData?.clients ?? []).filter((c) => !c.archived).length > 1;
  const tlabel = { font: "600 10px Sora", letterSpacing: ".06em", textTransform: "uppercase" as const, color: "var(--muted2)", whiteSpace: "nowrap" as const };

  const settled = totalOwedCents <= 0;
  const hero = settled
    ? {
        bg: "linear-gradient(135deg, rgba(34,197,94,.08), rgba(34,197,94,.02))",
        border: "rgba(34,197,94,.25)",
        glow: "rgba(34,197,94,.18)",
        color: "var(--success, #22c55e)",
        label2: "var(--success, #22c55e)",
        amount: "All clear",
        sub: "No outstanding invoices",
      }
    : {
        bg: "linear-gradient(135deg, rgba(239,68,68,.09), rgba(239,68,68,.02))",
        border: "rgba(239,68,68,.25)",
        glow: "rgba(239,68,68,.18)",
        color: "var(--danger, #ef4444)",
        label2: "var(--danger, #ef4444)",
        amount: formatEuros(totalOwedCents),
        sub: "Outstanding across all invoices",
      };

  return (
    <div className="page-content">
      {/* ── Toolbar: two orthogonal axes (Client filter · Grouper) + actions ──
          One cluster, so "whose payments" and "how they bucket" never blur. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 16px", alignItems: "center", marginBottom: 12 }}>
        {hasMultipleClients && (
          <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
            <span style={tlabel}>{t("clientFilter.label")}</span>
            <ClientFilter value={clientId} onChange={setClientId} />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={tlabel}>{t("payments.group")}</span>
          <GrouperSegment mode={mode} onChange={setMode} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          <button
            onClick={() => { setShowImport((v) => !v); setShowAdd(false); }}
            style={{ height: 40, padding: "0 15px", display: "inline-flex", alignItems: "center", gap: 7, border: "1px solid var(--border)", borderRadius: 10, background: "var(--card)", color: "var(--text)", font: "600 13px var(--font-head)", cursor: "pointer" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 16V4M7 9l5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
            </svg>
            {t("importCard.trigger")}
          </button>
          <button
            className="btn primary"
            onClick={() => { setShowAdd((v) => !v); setShowImport(false); }}
            style={{ height: 40, padding: "0 15px", gap: 7 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add payment
          </button>
        </div>
      </div>

      {/* ── Scoped context row — only when a single client is filtered ────── */}
      {clientId && (
        <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 14px", marginBottom: 14, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }}>
          <span style={{ width: 34, height: 34, flex: "none", borderRadius: "50%", background: "color-mix(in srgb, var(--primary) 16%, transparent)", color: "var(--primary-deep, var(--primary))", display: "flex", alignItems: "center", justifyContent: "center", font: "700 15px Sora" }}>
            {filterInitial}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ font: "600 15px Sora", color: "var(--text)", lineHeight: 1.2 }}>{filterName}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
              {t("payments.filteredCount", { count: payments.length })}
            </div>
          </div>
          <button
            aria-label={t("clientFilter.all")}
            onClick={() => setClientId("")}
            style={{ width: 32, height: 32, flex: "none", border: "1px solid var(--border)", borderRadius: 10, background: "var(--card)", color: "var(--muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Hero: owed summary ───────────────────────────── */}
      <div
        style={{
          borderRadius: "16px",
          padding: "18px 20px",
          marginBottom: "14px",
          background: hero.bg,
          border: `1px solid ${hero.border}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: "-26px",
            top: "-26px",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${hero.glow}, transparent 70%)`,
          }}
        />
        <div
          style={{
            font: "600 11px/14px var(--font-head)",
            letterSpacing: ".07em",
            textTransform: "uppercase",
            color: hero.label2,
            position: "relative",
          }}
        >
          Owed to you
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            fontSize: "32px",
            lineHeight: 1.15,
            color: hero.color,
            marginTop: "6px",
            position: "relative",
          }}
          aria-label={`Total owed: ${hero.amount}`}
        >
          {isLoading ? "…" : hero.amount}
        </div>
        <div
          style={{
            fontSize: "13px",
            color: "var(--muted)",
            marginTop: "3px",
            position: "relative",
          }}
        >
          {hero.sub}
        </div>
      </div>

      {/* ── CSV import (inline) ──────────────────────────── */}
      {showImport && <ImportCard only="payments" title={t("importCard.titlePayments")} />}

      {/* ── Add payment form (inline) ────────────────────── */}
      {showAdd && <AddPaymentForm onClose={() => setShowAdd(false)} />}

      {/* ── Period summary (year → month) ────────────────── */}
      {!isLoading && payments.length > 0 && <PeriodSummary payments={payments} mode={mode} />}

      {/* ── Payment list ─────────────────────────────────── */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
          Loading…
        </div>
      ) : payments.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: "var(--card)",
            borderRadius: "16px",
            border: "1px dashed var(--border)",
            color: "var(--muted)",
          }}
        >
          <div style={{ fontSize: "32px", marginBottom: "12px" }}>💸</div>
          <div style={{ fontWeight: 600, fontSize: "15px", marginBottom: "6px" }}>
            {clientId ? t("clientFilter.noPayments", { name: filterName }) : "No payments yet"}
          </div>
          <div style={{ fontSize: "13px" }}>
            {clientId ? t("clientFilter.noPaymentsBody") : "Add your first payment to start tracking invoices."}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {payments.map((p) => (
            <PaymentRow key={p.id} payment={p} />
          ))}
        </div>
      )}

      {/* ── Coming soon note ─────────────────────────────── */}
      <div
        style={{
          marginTop: "14px",
          padding: "13px 15px",
          borderRadius: "12px",
          border: "1px dashed var(--border)",
          background: "var(--surface)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          color: "var(--muted2)",
          fontSize: "12.5px",
        }}
      >
        <span
          style={{
            font: "600 9px var(--font-head)",
            letterSpacing: ".05em",
            textTransform: "uppercase",
            background: "var(--card)",
            border: "1px solid var(--border)",
            color: "var(--muted2)",
            padding: "2px 7px",
            borderRadius: "6px",
            flexShrink: 0,
          }}
        >
          Soon
        </span>
        Partial-payment splits, invoice refs &amp; PDFs
      </div>
    </div>
  );
}
